import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import api, { errMsg } from "../api";

function notificationLabel(type, lang) {
    const labels = {
        assigned: lang === "de" ? "Dir zugewiesen" : "Assigned to you",
        mentioned: lang === "de" ? "Du wurdest erwähnt" : "You were mentioned",
        comment: lang === "de" ? "Neuer Kommentar" : "New comment",
        approval_requested: lang === "de" ? "Freigabe angefordert" : "Approval requested",
        invoice_assigned: lang === "de" ? "Rechnung zugewiesen" : "Invoice assigned",
        announcement: lang === "de" ? "Neue Mitteilung" : "New announcement",
        sc_conv_deletion_request: lang === "de" ? "Löschantrag offen" : "Deletion requested",
        deletion_requested: lang === "de" ? "Löschantrag" : "Deletion requested",
    };
    return labels[type] || (lang === "de" ? "Aktualisierung" : "Update");
}
import {
    LayoutDashboard, Calendar as CalendarIcon, Plus, Bell, LogOut,
    Settings, Briefcase, Menu, X, AtSign, Wallet, Megaphone, AlertTriangle, CheckSquare,
    Users as UsersIcon, Plane, Paperclip, Loader2, MessageSquare, MessageCircle, Clock, CalendarRange, Folder, Sparkles, StickyNote, Mail,
    PanelLeftClose, PanelLeftOpen, HelpCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
    Popover, PopoverContent, PopoverTrigger
} from "../components/ui/popover";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import TutorialModal from "../components/help/TutorialModal";
import HelpPanel from "../components/help/HelpPanel";
import useTutorial from "../hooks/useTutorial";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { LOCALES } from "../i18n";
import { toast, Toaster } from "sonner";
import TaskDetailDialog from "../components/TaskDetailDialog";
import FloatingChatWidget from "../components/FloatingChatWidget";
import TabsBar from "../components/TabsBar";

function NavItem({ to, icon: Icon, label, testId, end = false, onClick, collapsed = false }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            onClick={onClick}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-0" : "gap-2.5 px-3"} py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                        ? "bg-brand-900 text-white"
                        : "text-neutral-700 hover:bg-neutral-200/60"
                }`
            }
        >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
    );
}

const LANGS = ["tr", "en", "de"];

// Customisable nav items (Dashboard is fixed first and not in this list).
// `cond(user)` controls visibility based on role.
const NAV_CATALOG = [
    { id: "calendar",      to: "/calendar",      icon: CalendarIcon,    labelKey: "calendar",      testId: "nav-calendar" },
    { id: "banking",       to: "/banking",       icon: Wallet,          labelKey: "banking",       testId: "nav-banking" },
    { id: "employees",     to: "/employees",     icon: UsersIcon,       labelKey: "employees",     testId: "nav-employees" },
    { id: "vacations",     to: "/vacations",     icon: Plane,           labelKey: "vacations",     testId: "nav-vacations" },
    { id: "zeiterfassung", to: "/zeiterfassung", icon: Clock,           label: { de: "Zeiterfassung", en: "Time tracking", tr: "Mesai" }, testId: "nav-timetrack" },
    { id: "schichtplan",   to: "/schichtplan",   icon: CalendarRange,   label: { de: "Schichtplan",   en: "Shift planner", tr: "Vardiya" }, testId: "nav-shifts" },
    { id: "cases",         to: "/cases",         icon: HelpCircle,      label: { de: "Cases & Support", en: "Cases & Support", tr: "Destek" }, testId: "nav-cases" },
    { id: "drive",         to: "/drive",         icon: Folder,          label: "Drive",            testId: "nav-drive" },
    { id: "announcements", to: "/announcements", icon: Megaphone,       labelKey: "announcements", testId: "nav-announcements" },
    { id: "superchat",     to: "/superchat",     icon: MessageCircle,   labelKey: "superchat",     testId: "nav-superchat",
      cond: (u) => u?.role === "admin" || u?.role === "master" || (u?.superchat_channel_ids || []).length > 0 },
    { id: "whatsapp",      to: "/whatsapp",      icon: MessageCircle,   label: "Business WhatsApp", testId: "nav-whatsapp",
      cond: (u) => u?.role === "admin" || u?.role === "master" },
    { id: "claude",        to: "/claude",        icon: Sparkles,        label: "Claude AI",        testId: "nav-claude" },
    { id: "mail",          to: "/mail",          icon: Mail,            label: "E-Mail",           testId: "nav-mail" },
    { id: "notes",         to: "/notes",         icon: StickyNote,      label: "Notizblock",       testId: "nav-notes" },
    { id: "settings",      to: "/settings",      icon: Settings,        labelKey: "settings",      testId: "nav-settings" },
    { id: "admin",         to: "/admin",         icon: Settings,        labelKey: "admin",         testId: "nav-admin",
      cond: (u) => u?.role === "admin" || u?.role === "master" },
];
export const NAV_CATALOG_IDS = NAV_CATALOG.map((n) => n.id);

function resolveLabel(item, lang) {
    if (item.labelKey) return t(lang, item.labelKey);
    if (typeof item.label === "string") return item.label;
    if (item.label) return item.label[lang] || item.label.en || item.id;
    return item.id;
}

export function orderedNavItems(user) {
    const stored = Array.isArray(user?.nav_order) ? user.nav_order : [];
    const valid = stored.filter((id) => NAV_CATALOG_IDS.includes(id));
    const missing = NAV_CATALOG_IDS.filter((id) => !valid.includes(id));
    const orderedIds = [...valid, ...missing];
    return orderedIds.map((id) => NAV_CATALOG.find((n) => n.id === id)).filter(Boolean);
}

export default function AppLayout({ children }) {
    const { user, logout, lang, setLang } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [spaces, setSpaces] = useState([]);
    const [users, setUsers] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [newSpaceOpen, setNewSpaceOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try { return localStorage.getItem("mytacs-sidebar-collapsed") === "1"; } catch (_) { return false; }
    });
    const toggleSidebar = () => {
        setSidebarCollapsed((v) => {
            const nv = !v;
            try { localStorage.setItem("mytacs-sidebar-collapsed", nv ? "1" : "0"); } catch (_) { /* ignore */ }
            return nv;
        });
    };
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [pendingAnns, setPendingAnns] = useState([]);
    const [annDialogOpen, setAnnDialogOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const tutorial = useTutorial();

    const taskParam = searchParams.get("task");

    const loadPendingAnns = useCallback(async () => {
        try {
            const { data } = await api.get("/announcements/pending");
            setPendingAnns(data);
        } catch { /* ignore */ }
    }, []);

    const loadSpaces = useCallback(async () => {
        const { data } = await api.get("/spaces");
        setSpaces(data);
    }, []);

    const loadUsers = useCallback(async () => {
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch { /* ignore */ }
    }, []);

    const loadNotifs = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifications((prev) => {
                // Toast on truly new unread notifications (not in previous batch)
                const prevIds = new Set(prev.map((p) => p.id));
                const fresh = (data || []).filter((n) => !n.read && !prevIds.has(n.id));
                fresh.forEach((n) => {
                    const title = n.task_title || (lang === "de" ? "Neue Mitteilung" : "New notification");
                    const subject = n.by_name ? `${n.by_name}: ${title}` : title;
                    toast(subject, { description: notificationLabel(n.type, lang), duration: 6000 });
                });
                return data || [];
            });
            // Broadcast event so other pages can re-fetch their data instantly
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("mytacs:tick"));
            }
        } catch { /* ignore */ }
    }, [lang]);

    useEffect(() => {
        loadSpaces();
        loadUsers();
        loadNotifs();
        loadPendingAnns();
        // Real-time-ish: poll every 5 seconds in the background
        const id = setInterval(loadNotifs, 5000);
        const id2 = setInterval(loadPendingAnns, 30000);
        return () => { clearInterval(id); clearInterval(id2); };
    }, [loadSpaces, loadUsers, loadNotifs, loadPendingAnns]);

    const ackAnnouncement = async (annId) => {
        try {
            await api.post(`/announcements/${annId}/acknowledge`);
            setPendingAnns((prev) => prev.filter((a) => a.id !== annId));
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const openTaskFromNotif = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
        loadNotifs();
        setPopoverOpen(false);
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.set("task", n.task_id);
            return p;
        });
    };

    const closeTaskDialog = () => {
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete("task");
            return p;
        });
    };

    const handleTaskChange = () => {
        // notify any pages listening
        window.dispatchEvent(new CustomEvent("taskflow:task-changed"));
    };

    const unread = notifications.filter((n) => !n.read).length;

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        loadNotifs();
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const closeMobile = () => setMobileOpen(false);

    return (
        <div className="min-h-screen flex bg-white" data-testid="app-layout">
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/40 z-30"
                    onClick={closeMobile}
                    data-testid="mobile-overlay"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 ${sidebarCollapsed ? "w-14" : "w-64"} shrink-0 border-r border-neutral-200 bg-neutral-50/95 lg:bg-neutral-50/60 backdrop-blur-xl flex flex-col h-screen transition-[width,transform] duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="sidebar"
                data-collapsed={sidebarCollapsed ? "true" : "false"}
            >
                <div className={`h-14 ${sidebarCollapsed ? "px-2" : "px-4"} flex items-center gap-2 border-b border-neutral-200 ${sidebarCollapsed ? "justify-center" : "justify-between"}`}>
                    {!sidebarCollapsed && (
                        <div className="flex items-center gap-2.5">
                            <div className="h-7 px-1.5 rounded-md bg-black flex items-center justify-center">
                                <img src="/brand/dutacs-logo-200.png" alt="DuTACS" className="h-4 w-auto" draggable="false" />
                            </div>
                            <span className="font-display font-bold tracking-tight">MyTACS</span>
                        </div>
                    )}
                    {sidebarCollapsed && (
                        <div className="h-7 w-7 rounded-md bg-black flex items-center justify-center" title="DuTACS GmbH">
                            <img src="/brand/dutacs-icon-32.png" alt="DuTACS" className="h-5 w-5" draggable="false" />
                        </div>
                    )}
                    {/* Collapse toggle — desktop only */}
                    <button
                        onClick={toggleSidebar}
                        className={`hidden lg:flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-200/60 transition ${sidebarCollapsed ? "absolute top-3.5 -right-3 bg-white border border-neutral-200 shadow-sm" : ""}`}
                        data-testid="sidebar-collapse-toggle"
                        title={sidebarCollapsed ? t(lang, "expandSidebar") || "Sidebar einblenden" : t(lang, "collapseSidebar") || "Sidebar einklappen"}
                    >
                        {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
                    </button>
                    <button
                        className="lg:hidden text-neutral-500"
                        onClick={closeMobile}
                        data-testid="close-mobile-sidebar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className={`flex-1 overflow-y-auto ${sidebarCollapsed ? "p-1.5" : "p-3"} space-y-6`}>
                    <nav className="space-y-1">
                        <NavItem to="/" end icon={LayoutDashboard} label={t(lang, "dashboard")} testId="nav-dashboard" onClick={closeMobile} collapsed={sidebarCollapsed} />
                        {orderedNavItems(user).map((item) => {
                            if (item.cond && !item.cond(user)) return null;
                            return (
                                <NavItem
                                    key={item.id}
                                    to={item.to}
                                    icon={item.icon}
                                    label={resolveLabel(item, lang)}
                                    testId={item.testId}
                                    onClick={closeMobile}
                                    collapsed={sidebarCollapsed}
                                />
                            );
                        })}
                    </nav>

                    {!sidebarCollapsed && (
                        <div>
                            <div className="flex items-center justify-between px-3 mb-2">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                    {t(lang, "spaces")}
                                </span>
                                {user ? (
                                    <button
                                        data-testid="new-space-btn"
                                        onClick={() => setNewSpaceOpen(true)}
                                        className="text-neutral-500 hover:text-neutral-900"
                                        title={t(lang, "newSpace")}
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                ) : null}
                            </div>
                            <div className="space-y-0.5">
                                {spaces.length === 0 && (
                                    <p className="px-3 text-xs text-neutral-500">—</p>
                                )}
                                {spaces.map((s) => (
                                    <NavItem
                                        key={s.id}
                                        to={`/space/${s.id}`}
                                        icon={Briefcase}
                                        label={s.name}
                                        testId={`nav-space-${s.id}`}
                                        onClick={closeMobile}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {sidebarCollapsed && spaces.length > 0 && (
                        <div className="space-y-0.5 pt-2 border-t border-neutral-200/60">
                            {spaces.map((s) => (
                                <NavItem
                                    key={s.id}
                                    to={`/space/${s.id}`}
                                    icon={Briefcase}
                                    label={s.name}
                                    testId={`nav-space-${s.id}`}
                                    onClick={closeMobile}
                                    collapsed={true}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* User card */}
                <div className={`${sidebarCollapsed ? "p-1.5" : "p-3"} border-t border-neutral-200`}>
                    <div className={`flex items-center ${sidebarCollapsed ? "justify-center" : "gap-2.5 px-2"} py-2 rounded-md`} data-testid="sidebar-user">
                        <Avatar className="w-8 h-8 border border-neutral-200 flex-shrink-0" title={sidebarCollapsed ? user?.name : undefined}>
                            <AvatarFallback className="bg-brand-900 text-white text-xs">
                                {user?.name?.slice(0, 2)?.toUpperCase() || "U"}
                            </AvatarFallback>
                        </Avatar>
                        {!sidebarCollapsed && (
                            <>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-neutral-900 truncate">{user?.name}</p>
                                    <p className="text-xs text-neutral-500 truncate">{user?.role}</p>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    data-testid="logout-btn"
                                    className="text-neutral-500 hover:text-neutral-900 p-1"
                                    title={t(lang, "logout")}
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col min-w-0">
                <header className="h-14 px-4 sm:px-6 border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between" data-testid="header">
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="lg:hidden text-neutral-700 p-1.5 -ml-1.5"
                            data-testid="open-mobile-sidebar"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="text-sm text-neutral-500 truncate">
                            <span className="hidden sm:inline">{t(lang, "welcome")}, </span>
                            <span className="font-semibold text-neutral-900">{user?.name}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* lang toggle */}
                        <div className="flex items-center gap-0.5 text-xs border border-neutral-200 rounded-md p-0.5 bg-white">
                            {LANGS.map((l) => (
                                <button
                                    key={l}
                                    data-testid={`header-lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`px-2 py-1 rounded ${lang === l ? "bg-brand-900 text-white" : "text-neutral-600 hover:bg-neutral-50"}`}
                                >
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {/* Neuigkeit verfassen (admin/master) */}
                        {(user?.role === "admin" || user?.role === "master") && (
                            <button
                                onClick={() => setAnnDialogOpen(true)}
                                data-testid="open-new-announcement-btn"
                                className="hidden sm:flex items-center gap-1.5 px-3 h-9 rounded-md border border-neutral-200 hover:bg-neutral-50 text-sm text-neutral-700"
                                title={t(lang, "newAnnouncement")}
                            >
                                <Megaphone className="w-4 h-4" />
                                <span className="hidden md:inline">{t(lang, "newAnnouncement")}</span>
                            </button>
                        )}

                        {/* Help button */}
                        <button
                            data-testid="help-trigger"
                            onClick={() => setHelpOpen(true)}
                            className="w-9 h-9 rounded-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                            title="Hilfe & Tutorial"
                            aria-label="Hilfe öffnen"
                        >
                            <HelpCircle className="w-4 h-4 text-neutral-700" />
                        </button>

                        {/* Notifications */}
                        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    data-testid="notification-trigger"
                                    className="relative w-9 h-9 rounded-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                                >
                                    <Bell className="w-4 h-4 text-neutral-700" />
                                    {unread > 0 && (
                                        <span
                                            data-testid="notification-badge"
                                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center"
                                        >
                                            {unread}
                                        </span>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-popover">
                                <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                                    <p className="font-semibold text-sm">{t(lang, "notifications")}</p>
                                    {unread > 0 && (
                                        <button
                                            data-testid="mark-all-read-btn"
                                            onClick={markAllRead}
                                            className="text-xs text-neutral-600 hover:text-neutral-900"
                                        >
                                            {t(lang, "markAllRead")}
                                        </button>
                                    )}
                                </div>
                                <div className="max-h-80 overflow-y-auto" data-testid="notifications-scroll">
                                    {notifications.length === 0 ? (
                                        <p className="px-4 py-6 text-sm text-neutral-500 text-center">
                                            {t(lang, "noNotifications")}
                                        </p>
                                    ) : (
                                        <ul className="py-1">
                                            {notifications.map((n) => (
                                                <li
                                                    key={n.id}
                                                    data-testid={`notification-${n.id}`}
                                                >
                                                    <button
                                                        onClick={() => openTaskFromNotif(n)}
                                                        data-testid={`notification-click-${n.id}`}
                                                        className={`w-full text-left px-4 py-2.5 border-b border-neutral-100 hover:bg-neutral-50 flex items-start gap-2 ${!n.read ? "bg-amber-50/40" : ""}`}
                                                    >
                                                        {n.type === "mention" ? (
                                                            <AtSign className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                                        ) : (
                                                            <Bell className="w-3.5 h-3.5 text-neutral-500 mt-0.5 shrink-0" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-neutral-900">
                                                                <span className="font-semibold">{n.by_name}</span>{" "}
                                                                <span className="text-neutral-600">{t(lang, n.type === "mention" ? "mentionedYou" : "assignedYou")}:</span>{" "}
                                                                <span className="font-medium">{n.task_title}</span>
                                                            </p>
                                                            <p className="text-[11px] text-neutral-500 mt-0.5">
                                                                {new Date(n.created_at).toLocaleString(LOCALES[lang])}
                                                            </p>
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </header>

                <TabsBar />

                <div className="flex-1 overflow-auto">
                    {children}
                </div>
            </main>

            <NewSpaceDialog
                open={newSpaceOpen}
                onClose={() => setNewSpaceOpen(false)}
                onCreated={() => { loadSpaces(); setNewSpaceOpen(false); }}
                lang={lang}
            />
            <NewAnnouncementDialog
                open={annDialogOpen}
                onClose={() => setAnnDialogOpen(false)}
                lang={lang}
            />
            {/* Pending announcement popup — blocks UI until acknowledged */}
            <AnnouncementPopup
                announcement={pendingAnns[0] || null}
                onAck={ackAnnouncement}
                lang={lang}
            />
            {/* Floating chat button (bottom-right) */}
            <FloatingChatWidget />
            {/* Global task dialog opened via ?task=<id> (e.g. from notifications) */}
            <TaskDetailDialog
                taskId={taskParam}
                open={!!taskParam}
                onClose={closeTaskDialog}
                onChange={handleTaskChange}
                allUsers={users}
                spaces={spaces}
                onOpenTask={(id) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("task", id); return p; })}
            />
            {/* Tutorial — auto-opens on first login, manually replayable from Help panel */}
            <TutorialModal
                open={tutorial.open}
                onOpenChange={(v) => { if (!v) tutorial.close(); }}
                onDismiss={tutorial.dismiss}
            />
            {/* Slide-out help panel with AI chat + case form */}
            <HelpPanel
                open={helpOpen}
                onOpenChange={setHelpOpen}
                onReplayTutorial={tutorial.replay}
            />
            <Toaster position="bottom-right" />
        </div>
    );
}

function NewSpaceDialog({ open, onClose, onCreated, lang }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [color, setColor] = useState("#0A0A0A");
    const [busy, setBusy] = useState(false);
    const [users, setUsers] = useState([]);
    const [memberIds, setMemberIds] = useState([]);
    const [leaderIds, setLeaderIds] = useState([]);

    useEffect(() => {
        if (open) {
            api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
        } else {
            setMemberIds([]); setLeaderIds([]);
        }
    }, [open]);

    const toggleMember = (uid) => setMemberIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
    const toggleLeader = (uid) => {
        setLeaderIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
        // Leaders are auto-members
        setMemberIds((prev) => prev.includes(uid) ? prev : [...prev, uid]);
    };

    const submit = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            await api.post("/spaces", { name, description, color, member_ids: memberIds, leader_ids: leaderIds });
            toast.success(t(lang, "spaceName"));
            setName(""); setDescription(""); setColor("#0A0A0A");
            setMemberIds([]); setLeaderIds([]);
            onCreated();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="new-space-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "newSpace")}</DialogTitle>
                    <DialogDescription>{lang === "de" ? "Nur die hier gewählten Personen sehen die Abteilung. Auch andere Admins müssen explizit hinzugefügt werden." : "Only the people listed here will see the department. Other admins must be added explicitly."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "spaceName")}</Label>
                        <Input
                            data-testid="space-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Sales"
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "description")}</Label>
                        <Input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder=""
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "spaceColor")}</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            {["#0A0A0A", "#1d4ed8", "#15803d", "#b45309", "#be123c", "#7e22ce"].map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={`w-7 h-7 rounded-md border-2 ${color === c ? "border-neutral-900" : "border-neutral-200"}`}
                                    style={{ background: c }}
                                    data-testid={`space-color-${c}`}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Sichtbar für" : "Visible to"}</Label>
                        <p className="text-[11px] text-neutral-500 mt-0.5">{lang === "de" ? "Klick = Mitglied. Doppelklick = Abteilungsleiter." : "Click = member. Double-click = leader."}</p>
                        <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-h-44 overflow-y-auto border border-neutral-200 rounded-md p-2">
                            {users.map((u) => {
                                const isMember = memberIds.includes(u.id);
                                const isLeader = leaderIds.includes(u.id);
                                return (
                                    <button
                                        key={u.id}
                                        onClick={() => toggleMember(u.id)}
                                        onDoubleClick={() => toggleLeader(u.id)}
                                        data-testid={`space-member-${u.id}`}
                                        className={`text-left text-xs px-2 py-1.5 rounded border transition ${
                                            isLeader ? "bg-blue-900 text-white border-blue-900" :
                                            isMember ? "bg-blue-50 border-blue-300 text-blue-900" :
                                            "bg-white border-neutral-200 hover:bg-neutral-50"
                                        }`}
                                    >
                                        {isLeader && "👑 "}
                                        {u.name} <span className="opacity-60">({u.role})</span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-neutral-500 mt-1">
                            {memberIds.length} {lang === "de" ? "Mitglied(er)" : "member(s)"} · {leaderIds.length} {lang === "de" ? "Leiter" : "leader(s)"}
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="space-cancel-btn">{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !name.trim()} data-testid="space-create-btn" className="bg-brand-900 hover:bg-brand-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewAnnouncementDialog({ open, onClose, lang }) {
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [urgent, setUrgent] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [targetMode, setTargetMode] = useState("all");  // all | role | space | users
    const [targetRole, setTargetRole] = useState("user");
    const [targetSpaceIds, setTargetSpaceIds] = useState([]);
    const [targetUserIds, setTargetUserIds] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [sendEmail, setSendEmail] = useState(true);
    const [expiresAt, setExpiresAt] = useState("");
    const fileRef = React.useRef(null);

    useEffect(() => {
        if (open) {
            api.get("/spaces").then((r) => setSpaces(r.data)).catch(() => {});
            api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
        }
    }, [open]);

    const onPickFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const form = new FormData();
                form.append("file", f);
                const { data } = await api.post("/announcements/upload", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setAttachments((prev) => [...prev, data]);
            }
        } catch (e) {
            toast.error(errMsg(e, "Upload error"));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

    const submit = async () => {
        if (!title.trim() || !content.trim()) return;
        setBusy(true);
        try {
            const payload = { title, content, urgent, attachments, send_email: sendEmail };
            if (targetMode === "role") payload.target_role = targetRole;
            if (targetMode === "space") payload.target_space_ids = targetSpaceIds;
            if (targetMode === "users") payload.target_user_ids = targetUserIds;
            if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
            await api.post("/announcements", payload);
            toast.success(t(lang, "announcementSent"));
            setTitle(""); setContent(""); setUrgent(false); setAttachments([]);
            setTargetMode("all"); setTargetSpaceIds([]); setTargetUserIds([]); setExpiresAt("");
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="new-announcement-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Megaphone className="w-5 h-5" /> {t(lang, "newAnnouncement")}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">
                        {t(lang, "announcementDescr")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "title")}</Label>
                        <Input
                            data-testid="announcement-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t(lang, "annTitlePlaceholder")}
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "content")}</Label>
                        <Textarea
                            data-testid="announcement-content-input"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={t(lang, "annContentPlaceholder")}
                            rows={5}
                            className="mt-1.5"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Ablauf (optional)" : "Expires (optional)"}</Label>
                        <Input
                            type="datetime-local"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="mt-1.5"
                            data-testid="announcement-expires-input"
                        />
                        <p className="text-[11px] text-neutral-500 mt-1">{lang === "de" ? "Nach Ablauf wird die Mitteilung automatisch versteckt. Admins können den Verlauf weiterhin sehen." : "Hidden automatically after this time. Admins can still see history."}</p>
                    </div>

                    {/* Attachments */}
                    <div>
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider">
                                {t(lang, "attachments")} {attachments.length > 0 && <span className="text-neutral-500 font-normal">({attachments.length})</span>}
                            </Label>
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                data-testid="ann-attach-btn"
                                className="text-xs flex items-center gap-1 px-2 py-1 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
                            >
                                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                                {t(lang, "attach")}
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                onChange={onPickFile}
                                className="hidden"
                                multiple
                                data-testid="ann-file-input"
                            />
                        </div>
                        {attachments.length > 0 && (
                            <ul className="mt-2 space-y-1" data-testid="ann-attachments-list">
                                {attachments.map((a) => (
                                    <li key={a.id} className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5">
                                        <Paperclip className="w-3 h-3 text-neutral-500 shrink-0" />
                                        <span className="flex-1 truncate" title={a.name}>{a.name}</span>
                                        <span className="text-neutral-400 text-[10px]">{Math.round((a.size || 0) / 1024)} KB</span>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(a.id)}
                                            data-testid={`ann-remove-${a.id}`}
                                            className="text-neutral-400 hover:text-red-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="announcement-urgent-checkbox"
                            checked={urgent}
                            onChange={(e) => setUrgent(e.target.checked)}
                            className="w-4 h-4 rounded border-neutral-300"
                        />
                        <span className="flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                            <span>{t(lang, "markUrgent")}</span>
                        </span>
                    </label>

                    {/* Target picker */}
                    <div className="border-t border-neutral-200 pt-3">
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Empfänger" : "Recipients"}</Label>
                        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                            {[
                                { v: "all", l: lang === "de" ? "👥 Alle" : "Everyone" },
                                { v: "role", l: lang === "de" ? "🎯 Nach Rolle" : "By role" },
                                { v: "space", l: lang === "de" ? "🏢 Abteilung" : "Department" },
                                { v: "users", l: lang === "de" ? "👤 Personen" : "Persons" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    type="button"
                                    onClick={() => setTargetMode(o.v)}
                                    data-testid={`ann-target-${o.v}`}
                                    className={`text-xs px-2 py-1.5 rounded-md border ${targetMode === o.v ? "bg-brand-900 text-white border-neutral-900" : "border-neutral-300 hover:bg-neutral-50"}`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                        {targetMode === "role" && (
                            <select value={targetRole} onChange={(e) => setTargetRole(e.target.value)} className="mt-2 w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-md" data-testid="ann-target-role">
                                <option value="user">User</option>
                                <option value="master">Master</option>
                                <option value="admin">Admin</option>
                            </select>
                        )}
                        {targetMode === "space" && (
                            <div className="mt-2 flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto" data-testid="ann-target-spaces">
                                {spaces.map((s) => {
                                    const sel = targetSpaceIds.includes(s.id);
                                    return (
                                        <button key={s.id} type="button" onClick={() => setTargetSpaceIds((p) => sel ? p.filter((x) => x !== s.id) : [...p, s.id])}
                                            className={`text-xs px-2 py-1 rounded-md border ${sel ? "bg-blue-100 border-blue-300 text-blue-800" : "border-neutral-300 hover:bg-neutral-50"}`}>{s.name}</button>
                                    );
                                })}
                            </div>
                        )}
                        {targetMode === "users" && (
                            <div className="mt-2 max-h-[150px] overflow-y-auto border border-neutral-200 rounded-md divide-y divide-neutral-100" data-testid="ann-target-users">
                                {employees.map((u) => {
                                    const sel = targetUserIds.includes(u.id);
                                    return (
                                        <button key={u.id} type="button" onClick={() => setTargetUserIds((p) => sel ? p.filter((x) => x !== u.id) : [...p, u.id])}
                                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left ${sel ? "bg-blue-50" : "hover:bg-neutral-50"}`}>
                                            <input type="checkbox" checked={sel} readOnly className="w-3 h-3" />
                                            <span className="text-xs">{u.name}</span>
                                            {u.job_title && <span className="text-[10px] text-neutral-500">· {u.job_title}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                            data-testid="ann-send-email"
                            className="w-4 h-4 rounded border-neutral-300"
                        />
                        <span>📧 {lang === "de" ? "Zusätzlich per E-Mail benachrichtigen" : "Also notify by email"}</span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="announcement-cancel-btn">{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !title.trim() || !content.trim()} data-testid="announcement-send-btn" className="bg-brand-900 hover:bg-brand-800">
                        <Megaphone className="w-4 h-4 mr-1.5" /> {t(lang, "send")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AnnouncementPopup({ announcement, onAck, lang }) {
    if (!announcement) return null;
    return (
        <Dialog open={true} onOpenChange={() => { /* blocking — only ack closes */ }}>
            <DialogContent
                data-testid="announcement-popup"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                className={`max-w-lg border-2 ${announcement.urgent ? "border-red-500 shadow-red-200" : "border-neutral-300"}`}
            >
                <DialogHeader>
                    <DialogTitle className={`font-display flex items-center gap-2 ${announcement.urgent ? "text-red-700" : ""}`}>
                        {announcement.urgent ? (
                            <AlertTriangle className="w-6 h-6 text-red-600 animate-pulse" />
                        ) : (
                            <Megaphone className="w-6 h-6" />
                        )}
                        <span>{announcement.title}</span>
                        {announcement.urgent && (
                            <span className="ml-1 text-[10px] font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                                {t(lang, "urgent")}
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">
                        {t(lang, "from")} <span className="font-semibold">{announcement.created_by_name}</span>
                        {" · "}
                        {new Date(announcement.created_at).toLocaleString(LOCALES[lang])}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-3 px-1">
                    <p className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed" data-testid="announcement-popup-content">
                        {announcement.content}
                    </p>
                    {(announcement.attachments || []).length > 0 && (
                        <div className="mt-3 space-y-1" data-testid="popup-attachments">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{t(lang, "attachments")}</p>
                            {announcement.attachments.map((a) => (
                                <a
                                    key={a.id}
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.id}/download?auth=${encodeURIComponent(localStorage.getItem("taskflow_token") || "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`popup-att-${a.id}`}
                                    className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5 hover:bg-neutral-100"
                                >
                                    <Paperclip className="w-3 h-3 text-neutral-500" />
                                    <span className="flex-1 truncate text-blue-700 underline">{a.name}</span>
                                    <span className="text-neutral-400 text-[10px]">{Math.round((a.size || 0) / 1024)} KB</span>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        onClick={() => onAck(announcement.id)}
                        data-testid="announcement-ack-btn"
                        className={announcement.urgent ? "bg-red-600 hover:bg-red-700" : "bg-brand-900 hover:bg-brand-800"}
                    >
                        ✓ {t(lang, "acknowledge")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
